import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiFileText, FiClock, FiCheckSquare, FiAlertTriangle,
  FiDollarSign, FiTrendingUp, FiPlus, FiArrowRight,
  FiActivity, FiLayers, FiZap, FiRefreshCw,
  FiCalendar, FiPieChart, FiArrowUpRight, FiArrowDownRight,
  FiMinus, FiUserCheck, FiBookOpen, FiMessageSquare, FiEdit2,
} from 'react-icons/fi';
import { canAccessRoute } from '../../utils/permissions';
import { TEMPLATE_LIBRARY_ENABLED } from '../../config/features';
import { fetchDashboard } from '../../store/slices/dashboardSlice';
import { fetchMyTasks, selectMergedTasks, selectTasksLoaded } from '../../store/slices/tasksSlice';
import { formatWeekdayDate, formatDate } from '../../utils/dateFormat';
import { formatMoney, formatNumber, getCurrencySymbol } from '../../utils/numberFormat';
import { localizeSeeded } from '../FlowManagement/flowUtils';
import PageHeader from '../../components/PageHeader/PageHeader';
import StickyHeaderAction from '../../components/StickyHeaderAction';
import './Dashboard.scss';

// ─── Animated Counter ────────────────────────────────────────────────────────
function AnimatedCounter({ value, duration = 900, format }) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const numericValue = typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0;
    const start = performance.now();

    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(numericValue * eased);
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  const rendered = format ? format(display) : formatNumber(Math.round(display), '0');
  return <span>{rendered}</span>;
}

// ─── Mini Sparkline ──────────────────────────────────────────────────────────
function MiniSpark({ values, color, width = 72, height = 24 }) {
  if (!values || values.length < 2) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="mini-spark">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Donut Chart (status distribution) ───────────────────────────────────────
function DonutChart({ data, total, size = 132, stroke = 20 }) {
  const [animated, setAnimated] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAnimated(true), 120); return () => clearTimeout(t); }, []);
  if (!data.length || total === 0) return null;

  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  // Small gap between segments, skipped when a single segment fills the ring.
  const gap = data.length > 1 ? 2 : 0;
  let offset = 0;

  return (
    <svg
      width={size}
      height={size}
      className="dash-donut__svg"
      role="img"
      aria-label="Status distribution"
    >
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        {data.map((d) => {
          const dash = Math.max((d.value / total) * circ - gap, 0);
          const seg = (
            <circle
              key={d.key}
              cx={size / 2} cy={size / 2} r={r}
              fill="none"
              stroke={d.color}
              strokeWidth={stroke}
              strokeLinecap={gap ? 'butt' : 'round'}
              strokeDasharray={animated ? `${dash} ${circ - dash}` : `0 ${circ}`}
              strokeDashoffset={-offset}
              style={{ transition: 'stroke-dasharray 700ms cubic-bezier(0.4,0,0.2,1)' }}
            >
              <title>{`${d.label}: ${d.value}`}</title>
            </circle>
          );
          offset += dash + gap;
          return seg;
        })}
      </g>
    </svg>
  );
}

// ─── Progress Ring ────────────────────────────────────────────────────────────
function ProgressRing({ value, max, color, size = 44, strokeWidth = 2.5, label }) {
  const [animated, setAnimated] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAnimated(true), 200); return () => clearTimeout(t); }, []);
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const dash = pct * circ;

  return (
    <svg width={size} height={size} className="progress-ring">
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke="currentColor" strokeOpacity="0.1" strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={animated ? `${dash} ${circ}` : `0 ${circ}`}
        strokeLinecap="round"
        style={{
          transition: 'stroke-dasharray 700ms cubic-bezier(0.4,0,0.2,1) 0.2s',
          transform: 'rotate(-90deg)',
          transformOrigin: `${size / 2}px ${size / 2}px`,
        }}
      />
      {label != null && (
        <text x="50%" y="50%" textAnchor="middle" dy="0.35em" className="progress-ring__label">{label}</text>
      )}
    </svg>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status, t }) {
  const clsMap = {
    draft: 'draft', in_review: 'review', pending_approval: 'pending', pending_edit: 'pending',
    approved: 'approved', active: 'active', rejected: 'rejected',
    expired: 'expired', terminated: 'terminated', archived: 'archived',
  };
  const cls = clsMap[status] || 'default';
  const label = t(`dashboard.statuses.${status}`, { defaultValue: status?.replace(/_/g, ' ') || 'Unknown' });
  return <span className={`dash-badge dash-badge--${cls}`}>{label}</span>;
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
function Dashboard() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { data, loading } = useSelector((state) => state.dashboard);
  const user = useSelector((state) => state.auth?.user);
  const liveAccent = useSelector(
    (state) => state.settings?.branding?.accentColor
      || state.settings?.config?.branding?.accentColor
      || '#6366f1'
  );

  const [refreshing, setRefreshing] = useState(false);
  // The widget lists everything waiting on this user: approval/review steps,
  // opinion requests (Görüş Al), and contracts an approver sent back for
  // changes — merged newest-first, the same three sources My Tasks shows.
  // The list comes from the shared tasks slice: one request per page load,
  // shared with the sidebar badge and the approval notifier, and shown from
  // the last result straight away on a revisit.
  const pendingApprovals = useSelector(selectMergedTasks);
  const tasksLoaded = useSelector(selectTasksLoaded);

  useEffect(() => { dispatch(fetchDashboard()); }, [dispatch]);
  useEffect(() => { dispatch(fetchMyTasks()); }, [dispatch]);

  // The server's own count is a plain tally of pending approval records: it
  // misses opinions and returned contracts, and counts later steps of a
  // sequential run that are not this user's turn yet. Once the task lists are
  // in, count those instead so the KPI card, the widget's badge and My Tasks
  // all report the same number. Until then the server value avoids a flash of
  // zero — read defensively, since `data` is null until the first dashboard
  // fetch resolves and this sits above the loading guard.
  const pendingTaskCount = tasksLoaded
    ? pendingApprovals.length
    : (data?.pendingApprovals || 0);

  const handleRefresh = async () => {
    setRefreshing(true);
    await dispatch(fetchDashboard());
    setTimeout(() => setRefreshing(false), 500);
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return t('dashboard.goodMorning');
    if (h < 17) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  // Abbreviated KPI money — the decimal separator follows the region
  // (TR → ₺1,25M, US → $1.25M). The symbol is the tenant's REPORTING currency,
  // which is what the backend converted every contract amount into; hard-coding
  // "$" here labelled converted totals with the wrong currency.
  const reportingCurrency = data?.reportingCurrency || 'USD';
  const currencySymbol = getCurrencySymbol(reportingCurrency);
  const formatCurrency = (val) => {
    if (!val) return `${currencySymbol}0`;
    if (val >= 1_000_000) return `${currencySymbol}${formatNumber((val / 1_000_000).toFixed(2))}M`;
    if (val >= 1_000) return `${currencySymbol}${formatNumber((val / 1_000).toFixed(1))}K`;
    return `${currencySymbol}${formatMoney(val)}`;
  };

  if (loading && !data) {
    return (
      <div className="dash-loading">
        <div className="dash-loading__bar"><span /></div>
        <p>{t('dashboard.loadingDashboard')}</p>
      </div>
    );
  }
  if (!data) return null;

  const statusColors = {
    draft:            '#94a3b8',
    in_review:        '#3b82f6',
    pending_approval: '#f59e0b',
    pending_edit:     '#d97706',
    approved:         '#10b981',
    active:           liveAccent,
    rejected:         '#ef4444',
    expired:          '#f97316',
    terminated:       '#64748b',
    archived:         '#cbd5e1',
  };

  const statusData = Object.entries(data.contractsByStatus || {})
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([k, v]) => ({
      key: k,
      label: t(`dashboard.statuses.${k}`, { defaultValue: k.replace(/_/g, ' ') }),
      value: v,
      color: statusColors[k] || '#94a3b8',
    }));

  // The dominant slice of the donut always wears the tenant accent color.
  // If another status already used the accent, it inherits the dominant
  // slice's original color so no two slices match.
  if (statusData.length) {
    const originalColor = statusData[0].color;
    const accentOwner = statusData.find((d, i) => i > 0 && d.color === liveAccent);
    if (accentOwner) accentOwner.color = originalColor;
    statusData[0].color = liveAccent;
  }

  const totalContracts = data.totalContracts || 0;
  const activeCount = data.contractsByStatus?.active || 0;
  const pipelineTotal = statusData.reduce((s, d) => s + d.value, 0) || totalContracts || 1;

  const kpis = [
    {
      key: 'total',
      label: t('dashboard.totalContracts'),
      value: totalContracts,
      icon: FiFileText,
      color: liveAccent,
      spark: [12, 18, 14, 22, 19, 25, totalContracts],
      delta: '12%', dir: 'up',
      link: '/contracts',
    },
    {
      key: 'active',
      label: t('dashboard.active'),
      value: activeCount,
      icon: FiActivity,
      color: '#10b981',
      spark: [4, 8, 6, 12, 10, 14, activeCount],
      delta: '8%', dir: 'up',
      link: '/contracts',
    },
    {
      key: 'approvals',
      label: t('dashboard.pendingApprovals'),
      value: pendingTaskCount,
      icon: FiCheckSquare,
      color: liveAccent,
      spark: [2, 4, 3, 5, 4, 6, pendingTaskCount],
      delta: '33%', dir: 'down',
      link: '/approvals',
    },
    {
      key: 'expiring',
      label: t('dashboard.expiringSoonLabel'),
      value: data.expiringIn30Days || 0,
      icon: FiCalendar,
      color: '#f97316',
      spark: [3, 5, 4, 6, 5, 7, data.expiringIn30Days || 0],
      delta: '14%', dir: 'down',
      link: '/contracts',
    },
    {
      key: 'overdue',
      label: t('dashboard.overdueObligations'),
      value: data.overdueObligations || 0,
      icon: FiAlertTriangle,
      color: '#ef4444',
      spark: [1, 2, 1, 3, 2, 4, data.overdueObligations || 0],
      delta: '0%', dir: 'flat',
      link: '/contracts',
    },
  ];

  const expiryItems = [
    { label: t('dashboard.next30Days'), value: data.expiringIn30Days || 0, color: '#ef4444' },
    { label: t('dashboard.next60Days'), value: data.expiringIn60Days || 0, color: '#f59e0b' },
    { label: t('dashboard.next90Days'), value: data.expiringIn90Days || 0, color: '#14b8a6' },
  ];
  const maxExpiry = Math.max(...expiryItems.map(i => i.value), 1);

  // Permission-filtered: the Template Library entry only shows to users who
  // can actually open the page. The card keeps its size — the action list
  // renders as a two-column grid from 4 entries up (see .dash-actions--grid).
  const quickActions = [
    { label: t('dashboard.newContractAction'), icon: FiPlus,   to: '/contracts/new', color: liveAccent },
    { label: t('dashboard.allContracts'),      icon: FiLayers, to: '/contracts',     color: '#3b82f6'  },
    { label: t('nav.approvals'),               icon: FiUserCheck, to: '/approvals',  color: '#10b981'  },
    ...(TEMPLATE_LIBRARY_ENABLED
      ? [{ label: t('nav.templateLibrary'),    icon: FiBookOpen, to: '/template-library', color: '#8b5cf6' }]
      : []),
  ].filter(a => canAccessRoute(user?.role, a.to, user?.effectivePermissions || null));

  const now = new Date();

  // Relative due-date label for a pending approval. Returns { text, tone }.
  const getDueInfo = (d) => {
    if (!d) return { text: t('dashboard.noDueDate'), tone: 'muted' };
    const diff = Math.ceil((new Date(d) - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { text: t('dashboard.overdue'), tone: 'danger' };
    if (diff === 0) return { text: t('dashboard.dueToday'), tone: 'warning' };
    if (diff === 1) return { text: t('dashboard.dueTomorrow'), tone: 'warning' };
    return { text: t('dashboard.dueInDays', { count: diff }), tone: diff <= 3 ? 'warning' : 'muted' };
  };

  const dateStr = formatWeekdayDate(now);
  const totalValue = data.totalContractValue || 0;

  return (
    <div className="dash" style={{ '--ha': liveAccent }}>
      {/* ── Header ── */}
      {/* The dashboard keeps its personal greeting as the title — deliberately
          not the sidebar entry, unlike every other page. It is the landing
          screen, and the welcome is the point of it. Structure and spacing
          still come from the shared page header. */}
      <PageHeader
        className="dash-head"
        title={`${getGreeting()}${user?.firstName ? `, ${user.firstName}` : ''}`}
        description={t('dashboard.heroSubtitle')}
        actions={(
          <>
            <button
              type="button"
              className={`dash-btn dash-btn--icon${refreshing ? ' is-spinning' : ''}`}
              onClick={handleRefresh}
              aria-label={t('dashboard.refreshDashboard')}
              title={t('dashboard.refreshDashboard')}
            >
              <FiRefreshCw size={14} />
            </button>
            {/* Creating a contract is the point of this screen, so the action
                follows the user down the page: it docks into the header once
                it scrolls out of view and returns here on the way back up. */}
            <StickyHeaderAction accent={liveAccent}>
              <Link to="/contracts/new" className="dash-btn dash-btn--primary">
                <FiPlus size={14} />
                <span>{t('dashboard.newContractAction')}</span>
              </Link>
            </StickyHeaderAction>
          </>
        )}
        above={(
          <span className="dash-head__eyebrow">
            <FiCalendar size={11} />
            {dateStr}
          </span>
        )}
      />

      {/* ── KPI Row ── */}
      <section className="dash-kpis">
        {kpis.map((k, i) => (
          <Link
            key={k.key}
            to={k.link}
            className="dash-kpi"
            style={{ animationDelay: `${i * 50}ms` }}
          >
            <div className="dash-kpi__head">
              <span
                className="dash-kpi__tile"
                style={{
                  color: k.color,
                  background: `color-mix(in srgb, ${k.color} 12%, transparent)`,
                }}
              >
                <k.icon size={15} />
              </span>
              <span className="dash-kpi__label">{k.label}</span>
            </div>
            <div className="dash-kpi__value">
              <AnimatedCounter value={k.value} />
            </div>
            <div className="dash-kpi__foot">
              <div className="dash-kpi__trendcol">
                <span className={`dash-trend is-${k.dir}`}>
                  {k.dir === 'up' && <FiArrowUpRight size={10} />}
                  {k.dir === 'down' && <FiArrowDownRight size={10} />}
                  {k.dir === 'flat' && <FiMinus size={10} />}
                  {k.delta}
                </span>
                <span className="dash-kpi__vs">{t('dashboard.vsLast30Days')}</span>
              </div>
              <MiniSpark values={k.spark} color={k.color} />
            </div>
          </Link>
        ))}
      </section>

      {/* ── My Tasks / Pending Approvals + Contracts by Status ── */}
      <section className="dash-ops">

        {/* Pending Approvals */}
        <article className="dash-card dash-card--approvals">
          <div className="dash-card__head">
            <div className="dash-card__title-wrap">
              <FiCheckSquare size={13} className="dash-card__icon" />
              <h3 className="dash-card__title">{t('dashboard.myTasks')}</h3>
              {pendingApprovals.length > 0 && (
                <span className="dash-card__count">{pendingApprovals.length}</span>
              )}
            </div>
            <Link to="/approvals" className="dash-card__link">
              {t('dashboard.viewAll')} <FiArrowRight size={11} />
            </Link>
          </div>
          <ul className="dash-feed">
            {!tasksLoaded ? (
              // Same footprint as a real row, so the card does not jump when
              // the list lands.
              [0, 1, 2].map((i) => (
                <li key={`skeleton-${i}`} className="dash-feed__skeleton" aria-hidden="true">
                  <span className="dash-skel dash-skel--avatar" />
                  <span className="dash-feed__body">
                    <span className="dash-skel dash-skel--line" style={{ width: `${68 - i * 9}%` }} />
                    <span className="dash-skel dash-skel--line dash-skel--short" />
                  </span>
                  <span className="dash-skel dash-skel--chip" />
                </li>
              ))
            ) : pendingApprovals.length === 0 ? (
              <li className="dash-empty">
                <FiCheckSquare size={26} />
                <p>{t('dashboard.noPendingApprovals')}</p>
              </li>
            ) : (
              pendingApprovals.slice(0, 3).map((a, i) => {
                const c = a.contractId || {};
                const stepLabel = a.isEditTask
                  ? t('approvals.editStep')
                  : (a.stepName ? localizeSeeded(a.stepName, i18n.language) : null);
                const title = c.contractName || c.counterpartyName || c.contractNumber || stepLabel || '—';
                // Opinion requests and returned contracts carry no deadline —
                // the chip names the task kind instead of a due date.
                let due;
                if (a.isOpinionTask) due = { text: t('approvals.state.opinion'), tone: 'muted' };
                else if (a.isEditTask) due = { text: t('approvals.state.edit'), tone: 'warning' };
                else due = getDueInfo(a.dueAt);
                // The row opens the contract itself so the pending action can
                // be taken right there; only the "view all" links below go to
                // the My Tasks list. Carries the same navigation context the
                // list would, so the detail page's back button returns to
                // My Tasks > Pending rather than the Contracts list.
                return (
                  <li key={a._id} style={{ animationDelay: `${i * 40}ms` }}>
                    <Link
                      to={c._id ? `/contracts/${c._id}` : '/approvals'}
                      state={c._id ? { navSource: '/approvals', backTo: { path: '/approvals', tab: 'pending' } } : undefined}
                      className="dash-feed__item"
                    >
                      <span className="dash-feed__avatar dash-feed__avatar--approval">
                        {a.isOpinionTask
                          ? <FiMessageSquare size={14} />
                          : a.isEditTask
                            ? <FiEdit2 size={14} />
                            : <FiCheckSquare size={14} />}
                      </span>
                      <span className="dash-feed__body">
                        <span className="dash-feed__title">
                          {title}
                          {c.versionType === 'addendum' && (
                            <span className="badge badge-addendum" style={{ marginLeft: 6 }}>
                              {t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })}
                            </span>
                          )}
                        </span>
                        <span className="dash-feed__meta">
                          {stepLabel && (
                            <>
                              <span className="dash-feed__step">{stepLabel}</span>
                              <span className="dash-feed__dot">·</span>
                            </>
                          )}
                          <span>{c.contractNumber}</span>
                        </span>
                      </span>
                      <span className={`dash-due dash-due--${due.tone}`}>
                        <FiClock size={10} />
                        {due.text}
                      </span>
                    </Link>
                  </li>
                );
              })
            )}
          </ul>
          <Link to="/approvals" className="dash-card__footlink">
            {t('dashboard.viewAllTasks')} <FiArrowRight size={11} />
          </Link>
        </article>

        {/* Status Donut */}
        <article className="dash-card dash-card--pipeline">
          <div className="dash-card__head">
            <div className="dash-card__title-wrap">
              <FiPieChart size={13} className="dash-card__icon" />
              <h3 className="dash-card__title">{t('dashboard.contractsByStatus')}</h3>
            </div>
            <span className="dash-card__count">{pipelineTotal}</span>
          </div>
          <div className="dash-donut">
            <DonutChart data={statusData} total={pipelineTotal} />
            <ul className="dash-legend">
              {statusData.slice(0, 5).map(d => (
                <li key={d.key} className="dash-legend__item">
                  <span className="dash-legend__dot" style={{ background: d.color }} />
                  <span className="dash-legend__label">{d.label}</span>
                  <span className="dash-legend__val">
                    {d.value} ({Math.round((d.value / pipelineTotal) * 100)}%)
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <Link to="/contracts" className="dash-card__footlink">
            {t('dashboard.viewAll')} <FiArrowRight size={11} />
          </Link>
        </article>

      </section>

      {/* ── Contract Overview / Analytics ── */}
      <section className="dash-bento">

        {/* Financial */}
        <article className="dash-card dash-card--value">
          <div className="dash-card__head">
            <div className="dash-card__title-wrap">
              <FiDollarSign size={13} className="dash-card__icon" />
              <h3 className="dash-card__title">{t('dashboard.totalValue')}</h3>
            </div>
          </div>
          <div className="dash-card__big">
            <AnimatedCounter value={totalValue} format={formatCurrency} duration={1100} />
          </div>
          <div className="dash-card__foot">
            <div className="dash-kpi__trendcol">
              <span className="dash-trend is-up">
                <FiTrendingUp size={11} /> 8.2%
              </span>
              <span className="dash-kpi__vs">{t('dashboard.vsLast30Days')}</span>
            </div>
            <MiniSpark
              values={[50, 80, 65, 90, 75, 110, Math.min(totalValue / 10000 + 30, 200)]}
              color="#10b981"
              width={120} height={40}
            />
          </div>
        </article>

        {/* Expiry Timeline */}
        <article className="dash-card dash-card--expiry">
          <div className="dash-card__head">
            <div className="dash-card__title-wrap">
              <FiCalendar size={13} className="dash-card__icon" />
              <h3 className="dash-card__title">{t('dashboard.expiryTimeline')}</h3>
            </div>
          </div>
          <ul className="dash-expiry">
            {expiryItems.map((it, i) => (
              <li key={it.label} className="dash-expiry__item" style={{ animationDelay: `${i * 60}ms` }}>
                <ProgressRing
                  value={it.value}
                  max={maxExpiry}
                  color={it.color}
                  size={40}
                  label={it.value}
                />
                <div className="dash-expiry__text">
                  <span className="dash-expiry__label">{it.label}</span>
                  <span className="dash-expiry__sub">
                    {it.value === 0
                      ? t('dashboard.noContractsExpiring')
                      : t('dashboard.contractCount', { count: it.value })}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <Link to="/contracts" className="dash-card__footlink">
            {t('dashboard.viewAll')} <FiArrowRight size={11} />
          </Link>
        </article>

        {/* Quick Actions */}
        <article className="dash-card dash-card--actions">
          <div className="dash-card__head">
            <div className="dash-card__title-wrap">
              <FiZap size={13} className="dash-card__icon" />
              <h3 className="dash-card__title">{t('dashboard.quickActions')}</h3>
            </div>
          </div>
          <div className={`dash-actions ${quickActions.length > 3 ? 'dash-actions--grid' : ''}`}>
            {quickActions.map((a, i) => (
              <Link key={a.to} to={a.to} className="dash-action" style={{ animationDelay: `${i * 40}ms` }}>
                <span
                  className="dash-action__icon"
                  style={{
                    color: a.color,
                    background: `color-mix(in srgb, ${a.color} 10%, transparent)`,
                    borderColor: `color-mix(in srgb, ${a.color} 22%, transparent)`,
                  }}
                >
                  <a.icon size={14} />
                </span>
                <span className="dash-action__label">{a.label}</span>
                <FiArrowRight size={12} className="dash-action__arrow" />
              </Link>
            ))}
          </div>
        </article>

      </section>

      {/* ── Recent Contracts ── */}
      <section className="dash-bento">

        {/* Recent Contracts */}
        <article className="dash-card dash-card--recent">
          <div className="dash-card__head">
            <div className="dash-card__title-wrap">
              <FiActivity size={13} className="dash-card__icon" />
              <h3 className="dash-card__title">{t('dashboard.recentContracts')}</h3>
            </div>
            <Link to="/contracts" className="dash-card__link">
              {t('dashboard.viewAll')} <FiArrowRight size={11} />
            </Link>
          </div>
          {(data.recentContracts || []).length === 0 ? (
            <div className="dash-empty">
              <FiFileText size={26} />
              <p>{t('dashboard.noContractsYet')}</p>
              <Link to="/contracts/new" className="dash-empty__link">
                {t('dashboard.createFirstContract')} <FiArrowRight size={11} />
              </Link>
            </div>
          ) : (
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>{t('dashboard.colContractNo')}</th>
                    <th>{t('dashboard.colContractName')}</th>
                    <th>{t('contracts.columns.company')}</th>
                    <th>{t('dashboard.colCounterparty')}</th>
                    <th>{t('dashboard.status')}</th>
                    <th>{t('dashboard.colLastUpdated')}</th>
                    <th className="is-num">{t('dashboard.colValue')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recentContracts || []).slice(0, 5).map((c) => (
                    <tr key={c._id}>
                      <td>
                        <Link to={`/contracts/${c._id}`} className="dash-table__no">
                          <FiFileText size={13} />
                          <span>{c.contractNumber || '—'}</span>
                        </Link>
                      </td>
                      <td className="dash-table__name">{c.contractName || '—'}</td>
                      <td>{c.companyId?.name || '—'}</td>
                      <td>{c.counterpartyName || '—'}</td>
                      <td><StatusBadge status={c.status} t={t} /></td>
                      <td>{formatDate(c.updatedAt || c.createdAt)}</td>
                      <td className="is-num">
                        {c.contractAmount != null
                          ? `${getCurrencySymbol(c.currency || reportingCurrency)}${formatMoney(c.contractAmount)}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>

      </section>
    </div>
  );
}

export default Dashboard;
